import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import type { AgentMessageResponse } from '../types/agent';
import type { AgentProjectPickAction } from '../utils/agentShortcut';
import { AGENT_PROJECT_PICK_META } from '../utils/agentProjectPickMeta';
import { ensureAgentChatForProject } from '../utils/agentChatBootstrap';
import {
  AGENT_SCOPE_ALL,
  buildSuggestAssertionsPayload,
  buildSuggestAssertionsUserMessage,
  buildApplyDefaultHeadersUserMessage,
  buildApplyBearerUserMessage,
  useAgentSuggestAssertionsScope,
  type AgentScopeAll,
} from '../utils/agentSuggestAssertionsScope';
import AgentSuggestAssertionsScopeFields from './AgentSuggestAssertionsScopeFields';
import { normalizeAgentFormId, agentFormIdsMatch } from '../utils/agentApplyEnvUtils';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { notifyAgentSideEffectsFromAgent } from '../utils/environmentVariablesEvents';
import { AgentInlineSelect } from './ai-agent-ui';

const fieldGapSx = { mb: 1 };

export type AgentWidgetProjectPickFormProps = {
  action: AgentProjectPickAction;
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultCollectionId?: number | AgentScopeAll;
  defaultRequestId?: number | AgentScopeAll;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetProjectPickForm: React.FC<AgentWidgetProjectPickFormProps> = ({
  action,
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultCollectionId,
  defaultRequestId,
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const meta = AGENT_PROJECT_PICK_META[action];
  const { refreshProjects, setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const isSuggestAssertions = action === 'suggest_datasets';
  const isApplyDefaultHeaders = action === 'apply_default_headers';
  const isApplyBearer = action === 'apply_bearer';
  const usesCollectionRequestScope = isSuggestAssertions || isApplyDefaultHeaders || isApplyBearer;
  const suggestAssertionsScope = useAgentSuggestAssertionsScope(
    usesCollectionRequestScope ? selectedProjectId : '',
    {
      bodyMethodsOnly: false,
      initialCollectionId: defaultCollectionId,
      initialRequestId: defaultRequestId,
    },
  );

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      const preferredId = normalizeAgentFormId(defaultProjectId);
      if (preferredId === '') return list;
      if (list.some((p) => agentFormIdsMatch(p.id, preferredId))) return list;
      const name = defaultProjectName.trim() || `Project ${preferredId}`;
      return [...list, { id: preferredId, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      await refreshProjects();
      const fetched = await apiService.getProjects();
      setAvailableProjects(mergeProjectOption(fetched.map((p) => ({ id: p.id, name: p.name }))));
    } catch {
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [mergeProjectOption, refreshProjects]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    const preferredId = normalizeAgentFormId(defaultProjectId);
    if (preferredId !== '' && availableProjects.some((p) => agentFormIdsMatch(p.id, preferredId))) {
      setSelectedProjectId(preferredId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  const handleSubmit = async () => {
    if (!selectedProjectId) return;
    const projectLabel =
      availableProjects.find((p) => p.id === selectedProjectId)?.name ?? `project #${selectedProjectId}`;
    const collectionLabel =
      suggestAssertionsScope.collectionId === AGENT_SCOPE_ALL
        ? 'All Collections'
        : suggestAssertionsScope.collections.find((c) => c.id === suggestAssertionsScope.collectionId)?.name ??
          'All Collections';
    const requestLabel =
      suggestAssertionsScope.requestId === AGENT_SCOPE_ALL
        ? 'All Requests'
        : suggestAssertionsScope.requests.find((r) => r.id === suggestAssertionsScope.requestId)?.name ??
          'All Requests';
    const userMessageText = isSuggestAssertions
      ? buildSuggestAssertionsUserMessage(projectLabel, collectionLabel, requestLabel)
      : isApplyDefaultHeaders
        ? buildApplyDefaultHeadersUserMessage(projectLabel, collectionLabel, requestLabel)
        : isApplyBearer
          ? buildApplyBearerUserMessage(projectLabel, collectionLabel, requestLabel)
          : meta.userMessage(projectLabel);
    onUserMessage(userMessageText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      let targetChatId = chatId ?? undefined;
      if (targetChatId == null) {
        const ensured = await ensureAgentChatForProject(
          { id: selectedProjectId, name: projectLabel },
          undefined,
          { forceListed: true, preferChatId: chatId ?? undefined },
        );
        targetChatId = ensured.chatId;
      }
      const payload = usesCollectionRequestScope
        ? buildSuggestAssertionsPayload(
            Number(selectedProjectId),
            suggestAssertionsScope.collectionId,
            suggestAssertionsScope.requestId,
          )
        : { projectId: selectedProjectId };
      const res = await apiService.agentMessage({
        action,
        chatId: targetChatId,
        message: userMessageText,
        payload,
      });
      onChatIdChange?.(targetChatId);
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (!res.error) {
        notifyAgentSideEffectsFromAgent(action, payload as Record<string, unknown>, res.state);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      await onAgentReply(res);
      onClose();
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
        {meta.title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {meta.description}
      </Typography>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id={`widget-project-pick-${action}-project`}
          label="Project"
          fullWidth
          value={selectedProjectId === '' ? '' : selectedProjectId}
          disabled={busy}
          emptyLabel="No projects found"
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      {usesCollectionRequestScope && (
        <AgentSuggestAssertionsScopeFields
          layout="stack"
          collectionId={suggestAssertionsScope.collectionId}
          requestId={suggestAssertionsScope.requestId}
          collections={suggestAssertionsScope.collections}
          requests={suggestAssertionsScope.requests}
          onCollectionChange={suggestAssertionsScope.setCollectionId}
          onRequestChange={suggestAssertionsScope.setRequestId}
          disabled={busy || !selectedProjectId}
          loadingCollections={suggestAssertionsScope.loadingCollections}
          loadingRequests={suggestAssertionsScope.loadingRequests}
        />
      )}

      {availableProjects.length === 0 && !loadingProjects && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          No projects found. Create a project first.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy || !selectedProjectId}
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : meta.submitLabel}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetProjectPickForm;
